/** @format */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  afterRenderEffect,
} from '@angular/core';
import { CarouselButtonComponent, GoogleAdManagerComponent } from '@shared/components';
import {
  CategoryDetail,
  CategoryDetails,
  FacetVOValue,
  ProductList,
} from '@plp/data-access/interfaces';
import { PRODUCT_LIST_HEADER_TEXT } from '@plp/data-access/constants';
import { Chain, PrunedProductListSEO } from '@shared/data-access/interfaces';
import { FeatureConfigurationService, Logger, UtilityService } from '@shared/data-access/services';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import {
  searchInsteadClicked,
  selectFeatureFlags,
  selectProductListCategoryDetails,
  selectProductListFacetVOs,
} from '@plp/data-access/state';

import { CategoryBubbleComponent } from '../category-bubble/category-bubble.component';
import { HomefieldTextLinkModule } from '@ecomm-patterns-components';
import { PersonalizationDataKeys } from '@shared/data-access/enums';
import { PlpBreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { Store } from '@ngrx/store';
import { ProductListLayoutService } from '@plp/data-access/services';

@Component({
  selector: 'plp-components-product-list-header',
  standalone: true,
  imports: [
    CarouselButtonComponent,
    CategoryBubbleComponent,
    HomefieldTextLinkModule,
    PlpBreadcrumbComponent,
    NgTemplateOutlet,
    HmfActionTextComponent,
    GoogleAdManagerComponent,
  ],
  templateUrl: './product-list-header.component.html',
  styleUrl: './product-list-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListHeaderComponent implements AfterViewInit, OnDestroy {
  private readonly utilityService = inject(UtilityService);
  private readonly featureConfigurationService = inject(FeatureConfigurationService);
  private readonly chain = inject(Chain);
  private readonly store = inject(Store);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(Logger);
  private readonly productListLayoutService = inject(ProductListLayoutService);

  readonly productListSEOData = input<PrunedProductListSEO | null>(null);
  readonly isPlp3Template = input(false);
  readonly productListData = input<ProductList | null>(null);

  readonly bubbleWrapper = viewChild<ElementRef<HTMLDivElement>>('bubbleWrapper');

  private intersectionObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;

  readonly facetVOs = this.store.selectSignal(selectProductListFacetVOs);
  readonly productListCategoryDetails = this.store.selectSignal(selectProductListCategoryDetails);

  readonly pageType = computed(() =>
    this.productListData()?.searchVO?.searchTerm ? 'srlp' : 'plp',
  );

  readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

  readonly enableTooltipSurvey = computed(() => this.featureFlags().enableTooltipSurvey);
  readonly isEvergreenContentEnabled = computed(
    () => this.featureFlags().isEvergreenContentEnabled,
  );

  readonly narrowBanner = computed(() => {
    if (this.pageType() === 'plp' && this.productListData()) {
      return this.featureFlags().isPLPNarrowGAMBannerEnabled;
    } else if (this.pageType() === 'srlp') {
      return this.featureFlags().isSRLPNarrowGAMBannerEnabled;
    } else {
      return false;
    }
  });

  private readonly baseBubbleDetails = computed(() => {
    const categoryBubblesFacet = this.facetVOs()?.find(
      (facet) => facet.attrIdentifier === 'facetCatgroup',
    );
    const seoName = this.productListSEOData()?.name.replaceAll(/['\s]/g, '');

    if (categoryBubblesFacet) {
      const categoryDetails = untracked(() => this.productListCategoryDetails() ?? {});
      return this.generateBubbles(categoryBubblesFacet.valueList, categoryDetails, seoName);
    }

    return [];
  });

  // Category composition only (ignores dataEmAttr/seoName changes). Used as the
  // effect's tracked dependency so it doesn't re-fire when only display/analytics
  // metadata changes on baseBubbleDetails.
  private readonly categoryBubbleIdsSignature = computed(() => {
    const categoryBubblesFacet = this.facetVOs()?.find(
      (facet) => facet.attrIdentifier === 'facetCatgroup',
    );
    return categoryBubblesFacet?.valueList.map((value) => value.value).join(',') ?? '';
  });

  private readonly personalizedBubbleDetails = signal<CategoryDetail[] | null>(null);

  readonly displayBubbles = computed(() => {
    const bubbleDetails = this.personalizedBubbleDetails() ?? this.baseBubbleDetails();
    if (this.productListSEOData() && !this.isPlp3Template()) {
      return bubbleDetails;
    }
    // For other cases, only show personalized bubbles if they exist
    return bubbleDetails.filter((bubble) => bubble.isPersonalizedContent);
  });

  readonly loadingPersonalization = signal(true);

  readonly chainAbbr = this.chain.chainIdentifierAbbr;

  readonly showLeftArrow = signal(false);
  readonly showRightArrow = signal(false);

  readonly zeroResultsText = PRODUCT_LIST_HEADER_TEXT.zeroResults;

  readonly searchResultsText = computed(() => {
    const productList = this.productListData();
    const searchTypes = productList?.searchVO?.searchTypes ?? [];
    const searchTerm = productList?.searchVO?.searchTerm ?? '';
    const isFuzzy = productList?.fuzzyMatch || searchTypes.includes('FUZZY');
    const isSemantic = searchTypes.includes('SEMANTIC');
    const noResultsType = searchTypes.includes('No_RESULTS');
    const totalCount = productList?.totalCount ?? 0;
    const standardTitle =
      totalCount === 0
        ? PRODUCT_LIST_HEADER_TEXT.standardZeroResultsTitle(searchTerm)
        : PRODUCT_LIST_HEADER_TEXT.standardTitle(totalCount, searchTerm);

    if ((isFuzzy || isSemantic) && (noResultsType || totalCount === 0)) {
      return {
        title:
          this.chainAbbr === 'dsg' ? PRODUCT_LIST_HEADER_TEXT.dsgZeroResultsTitle : standardTitle,
        subtext: '',
        zeroResults: true,
      };
    }

    if (isFuzzy || isSemantic) {
      return {
        title: isFuzzy
          ? PRODUCT_LIST_HEADER_TEXT.fuzzyTitle(searchTerm)
          : PRODUCT_LIST_HEADER_TEXT.semanticTitle(searchTerm),
        subtext: isFuzzy
          ? PRODUCT_LIST_HEADER_TEXT.fuzzySubtext
          : PRODUCT_LIST_HEADER_TEXT.semanticSubtext,
        zeroResults: false,
      };
    }

    return {
      title: standardTitle,
      subtext: '',
      zeroResults: totalCount === 0 || noResultsType,
    };
  });

  constructor() {
    // Update bubbles with personalization if facetVOs change
    effect((onCleanup) => {
      // Track the category composition (not the seoName-derived dataEmAttr) so this
      // effect doesn't re-fire when only analytics metadata changes on baseBubbleDetails.
      this.categoryBubbleIdsSignature();

      // facetVOs is undefined until the store loads. Skip so we don't fire a
      // personalization call with an empty array.
      if (!isPlatformBrowser(this.platformId) || this.facetVOs() === undefined) {
        return;
      }

      // Read the current value without tracking it as a dependency - the effect
      // should only re-run when categoryBubbleIdsSignature changes, not on every
      // new baseBubbleDetails() array reference (e.g. from dataEmAttr updates).
      const baseBubbleDetails = untracked(() => this.baseBubbleDetails());

      let active = true;

      this.loadingPersonalization.set(true);

      const idleHandle = this.utilityService.runWhenIdle(() => {
        if (!active) {
          return;
        }

        this.featureConfigurationService
          .getPersonalization(PersonalizationDataKeys.MODIFY_BUBBLE_FILTERS, baseBubbleDetails)
          .then((result) => {
            if (!active || !result) {
              return;
            }

            this.personalizedBubbleDetails.set(result);
          })
          .catch((error) => {
            if (!active) {
              return;
            }

            this.logger.warn('Failed to apply personalization:', error);
          })
          .finally(() => {
            if (!active) {
              return;
            }

            this.loadingPersonalization.set(false);
          });
      });

      // Prevent things from firing if this is cleaned up earlier
      onCleanup(() => {
        active = false;
        this.utilityService.cancelRunWhenIdle(idleHandle);
      });
    });

    let previousBubbleCount = 0;

    // Recalculate arrows when displayBubbles changes
    afterRenderEffect({
      read: () => {
        const bubbles = this.displayBubbles();

        if (!isPlatformBrowser(this.platformId)) {
          return;
        }

        if (bubbles.length > 0) {
          this.recalculateArrows();
        } else if (previousBubbleCount > 0) {
          // If we are going from showing bubbles to not showing them, compensate for the loss of vertical space by restarting the scroll
          // Do this smoothly if we are in the middle of smooth-scrolling upwards
          const scrollDirection = untracked(() => this.productListLayoutService.scrollDirection());
          this.productListLayoutService.scrollToTop({
            scrollBehavior: scrollDirection === 'up' ? 'smooth' : 'instant',
          });
        }

        previousBubbleCount = bubbles.length;
      },
    });
  }

  ngAfterViewInit(): void {
    const bubbleWrapperElement = this.bubbleWrapper()?.nativeElement;
    if (isPlatformBrowser(this.platformId) && bubbleWrapperElement) {
      this.setupIntersectionObserver(bubbleWrapperElement);
      this.setupResizeObserver(bubbleWrapperElement);
    }
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  recalculateArrows(): void {
    const bubbleWrapper = this.bubbleWrapper()?.nativeElement;
    if (!bubbleWrapper) {
      return;
    }
    this.showLeftArrow.set(bubbleWrapper.scrollLeft >= 25);
    this.showRightArrow.set(
      bubbleWrapper.scrollLeft + bubbleWrapper.clientWidth <= bubbleWrapper.scrollWidth - 25 &&
        bubbleWrapper.scrollWidth > bubbleWrapper.offsetWidth,
    );
  }

  private setupIntersectionObserver(bubbleWrapper: HTMLDivElement): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.target === bubbleWrapper) {
          this.recalculateArrows();
        }
      },
      {
        root: null,
      },
    );

    this.intersectionObserver.observe(bubbleWrapper);
  }

  private setupResizeObserver(bubbleWrapper: HTMLDivElement): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.recalculateArrows();
    });

    this.resizeObserver.observe(bubbleWrapper);
  }

  private getFaceoutThumbnailBaseUrl(chainId: string): string {
    if (chainId === 'dsg') {
      return 'https://www.dickssportinggoods.com/wcsstore/DicksSportingGoods/';
    }
    if (chainId === 'gg') {
      return 'https://www.golfgalaxy.com/wcsstore/GolfGalaxy/';
    }
    return 'https://www.dickssportinggoods.com/wcsstore/DicksSportingGoods/';
  }

  private getBubbleThumbnail(thumbnail: string, topResultThumbnail: string): string | undefined {
    if (thumbnail) {
      if (thumbnail.includes('Attachment/')) {
        // thumbnail field contains WCS url need to generate faceOutImage path for WCS host

        return this.getFaceoutThumbnailBaseUrl(this.chainAbbr) + thumbnail;
      } else {
        // thumbnail field isn't a WCS url generate as Akamai Image Storage path
        return `https://dks.scene7.com/is/image/dkscdn/${thumbnail}?$DSG_ProductCard$`;
      }
    } else if (topResultThumbnail) {
      // topResultThumbnail is used as a fallback when a manual override thumbnail isn't set this generates a Akamai Image Storage path for the api generated faceout image
      return `https://dks.scene7.com/is/image/dkscdn/${this.imagineUrlifyFaceOutImage(topResultThumbnail)}?$DSG_ProductCard$`;
    }
  }

  private generateBubbles(
    facetValueList: FacetVOValue[],
    catDetails: CategoryDetails,
    seoName?: string,
  ): CategoryDetail[] {
    const catBubbles: CategoryDetail[] = [];

    facetValueList.forEach((facetValue, index) => {
      if (facetValue) {
        const catDetail = catDetails[facetValue.value];
        if (catDetail) {
          const bubble = {
            ...catDetail,
            imgSrc: this.getBubbleThumbnail(catDetail.thumbnail, catDetail.topResultThumbnail),
          };
          if (seoName) {
            bubble.dataEmAttr = `LN_Bubble_${seoName}_${catDetail.name.replaceAll(/['\s]/g, '')}_${index + 1}`;
          }
          catBubbles.push(bubble);
        }
      }
    });

    return catBubbles;
  }

  private imagineUrlifyFaceOutImage(value: string): string {
    // This function accepts a string and first replaces any whitespace character with an underscore ( _ ).
    // Next the function looks for places where there are more than one underscore ( _ ) directly beside to each other it removes the extra underscores.
    return value.replaceAll(/\W/gi, '_').replaceAll(/(_+)\1+/g, '$1');
  }

  searchInstead(): void {
    const searchTerm = this.productListData()?.didYouMeanVO?.originalSearchTerm;
    if (!searchTerm) {
      return;
    }
    this.store.dispatch(searchInsteadClicked({ searchTerm }));
  }

  clickBubblesCarouselButton(isPrevious: boolean): void {
    const bubbleWrapper = this.bubbleWrapper()?.nativeElement;

    if (!bubbleWrapper) {
      return;
    }

    const newScrollLeft = isPrevious
      ? bubbleWrapper.scrollLeft - 150
      : bubbleWrapper.scrollLeft + 150;

    bubbleWrapper.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }
}
